// Composant d'upload de documents attaché à une section spécifique
import React from 'react';
import styled from 'styled-components';
import { FiUpload, FiFile, FiX, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { theme } from '@theme';

export interface UploadedDocument {
  section?: string;
  type: string;
  file: File;
  preview?: string;
}

export interface DocumentConfig {
  type: string;
  label: string;
  description: string;
  required: boolean;
}

interface DocumentUploadProps {
  sectionTitle: string;
  documents: DocumentConfig[];
  uploadedFiles: UploadedDocument[];
  onFileSelect: (documentType: string, file: File | null) => void;
  onFileRemove: (documentType: string) => void;
  readonly?: boolean;
}

const Container = styled.div`
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.border};
`;

const SectionHeader = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h4`
  font-size: ${theme.typography.sizes.md};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.info};
  }
`;

const SectionDescription = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0;
`;

const DocumentsList = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
`;

const DocumentItem = styled.div`
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.background};
  }

  &.uploaded {
    border-style: solid;
    border-color: ${theme.colors.success};
    background: ${theme.colors.success}10;
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
`;

const DocumentInfo = styled.div`
  flex: 1;
`;

const DocumentLabel = styled.div`
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const DocumentDescription = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.textSecondary};
`;

const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: 2px ${theme.spacing.sm};
  background: ${theme.colors.error}10;
  color: ${theme.colors.error};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
`;

const OptionalBadge = styled.span`
  display: inline-flex;
  padding: 2px ${theme.spacing.sm};
  background: ${theme.colors.info}10;
  color: ${theme.colors.info};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
`;

const UploadArea = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  color: ${theme.colors.primary};
  font-weight: ${theme.typography.weights.medium};
  font-size: ${theme.typography.sizes.sm};

  &:hover {
    background: ${theme.colors.primary}10;
    border-color: ${theme.colors.primary};
  }

  input[type="file"] {
    display: none;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const UploadedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: white;
  border: 1px solid ${theme.colors.success}40;
  border-radius: ${theme.borderRadius.sm};
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: ${theme.colors.success}20;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.success};
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.sizes.sm};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.textSecondary};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${theme.colors.error}10;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.error};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${theme.colors.error}20;
  }
`;

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  sectionTitle,
  documents,
  uploadedFiles = [],
  onFileSelect,
  onFileRemove,
  readonly = false
}) => {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getUploadedFile = (documentType: string) => {
    return uploadedFiles?.find(file => file.type === documentType);
  };

  if (documents.length === 0) return null;

  return (
    <Container>
      <SectionHeader>
        <SectionTitle>
          <FiFile />
          Documents pour {sectionTitle}
        </SectionTitle>
        <SectionDescription>
          Formats acceptés : PDF, JPG, PNG (max 5 Mo par fichier)
        </SectionDescription>
      </SectionHeader>

      <DocumentsList>
        {documents.map(doc => {
          const uploaded = getUploadedFile(doc.type);

          return (
            <DocumentItem key={doc.type} className={uploaded ? 'uploaded' : ''}>
              <DocumentHeader>
                <DocumentInfo>
                  <DocumentLabel>
                    {doc.label}
                    {doc.required ? (
                      <RequiredBadge>
                        <FiAlertCircle size={10} />
                        Obligatoire
                      </RequiredBadge>
                    ) : (
                      <OptionalBadge>Optionnel</OptionalBadge>
                    )}
                  </DocumentLabel>
                  <DocumentDescription>{doc.description}</DocumentDescription>
                </DocumentInfo>
              </DocumentHeader>

              {uploaded ? (
                <UploadedFileDisplay>
                  <FileIcon>
                    <FiCheck size={18} />
                  </FileIcon>
                  <FileInfo>
                    <FileName>{uploaded.file.name}</FileName>
                    <FileSize>{formatFileSize(uploaded.file.size)}</FileSize>
                  </FileInfo>
                  {!readonly && (
                    <RemoveButton
                      onClick={() => onFileRemove(doc.type)}
                      title="Supprimer"
                      type="button"
                    >
                      <FiX size={16} />
                    </RemoveButton>
                  )}
                </UploadedFileDisplay>
              ) : (
                <UploadArea className={readonly ? 'disabled' : ''}>
                  <FiUpload size={16} />
                  Choisir un fichier
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Vérifier la taille (5 Mo max)
                        if (file.size > 5 * 1024 * 1024) {
                          alert('Le fichier est trop volumineux (max 5 Mo)');
                          return;
                        }
                        onFileSelect(doc.type, file);
                      }
                    }}
                    disabled={readonly}
                  />
                </UploadArea>
              )}
            </DocumentItem>
          );
        })}
      </DocumentsList>
    </Container>
  );
};
